import React from "react";
import { fmtRM } from "../../lib/taxConfig";

const Row = ({ label, value, bold, indent, negative, section }) => (
  <div className={`flex items-center justify-between py-1.5 ${section ? "bg-[#f7f9fc] px-2 -mx-2 rounded" : ""} ${bold ? "font-semibold" : ""}`}>
    <span className={`text-sm ${indent ? "pl-4" : ""} ${bold ? "text-gray-800" : "text-gray-600"}`}>{label}</span>
    {value !== undefined && (
      <span className={`text-sm font-mono ${negative ? "text-red-600" : "text-gray-800"}`}>{fmtRM(Math.abs(value))}</span>
    )}
  </div>
);

const ResultsPanel = ({ results: r }) => {
  if (!r) return <div className="text-sm text-gray-400 text-center py-10">Enter figures to see the computation.</div>;

  return (
    <div className="text-sm">
      <Row label="BUSINESS INCOME" section bold />
      <Row label="Net profit/(loss) as per accounts" value={r.netProfit} negative={r.netProfit < 0} />
      <Row label="Less: Other income" value={r.otherIncomeTotal} indent />
      <Row label="Business income/(loss)" value={r.businessIncomeLoss} bold negative={r.businessIncomeLoss < 0} />
      {!!r.interestRestriction && <Row label="Add: Interest restriction S.33(2)" value={r.interestRestriction} indent />}
      <Row label="Add: Disallowable expenses" value={r.totalDisallowed} indent />
      <Row label="Less: Special deductions (S.111/132/157)" value={r.totalSpecialDeductions} indent />
      <Row label={r.adjustedIsLoss ? "ADJUSTED LOSS" : "ADJUSTED INCOME"} value={r.adjustedIncome} bold negative={r.adjustedIncome < 0} />

      <div className="border-t border-gray-200 my-2" />
      {!!r.balancingCharge && <Row label="Add: Balancing charge" value={r.balancingCharge} indent />}
      <Row label="Less: Capital allowance b/f" value={r.capitalAllowanceBf} indent />
      <Row label="Less: Capital allowance current" value={r.capitalAllowanceCurrent} indent />
      <Row label="CA absorbed this year" value={r.capitalAllowanceAbsorbed} indent />
      <Row label="STATUTORY INCOME" value={r.statutoryIncome} bold />
      <Row label="CA carried forward" value={r.capitalAllowanceCf} indent />

      <div className="border-t border-gray-200 my-2" />
      <Row label="Aggregate statutory business income" value={r.aggregateStatutoryBusinessIncome} />

      {(r.unabsorbedLossBf > 0 || r.lossBfAbsorbed > 0 || r.cyLossCf > 0 || r.unabsorbedLossCf > 0) && (
        <>
          <Row label="Less: UNABSORBED BUSINESS LOSSES" section bold />
          <Row label="Balance brought forward" value={r.unabsorbedLossBf} indent />
          <Row label="Amount absorbed this year" value={r.lossBfAbsorbed} indent negative />
          {r.cyLossCf > 0 && <Row label="Balance transferred from current year loss" value={r.cyLossCf} indent />}
          <Row label="Balance carried forward" value={r.unabsorbedLossCf} indent bold />
        </>
      )}

      <Row label="Add: Other statutory income" value={r.otherIncomeTotal} indent />
      <Row label="AGGREGATE INCOME" value={r.aggregateIncome} bold />

      {r.adjustedIsLoss ? (
        <>
          <Row label="Less: CURRENT YEAR LOSS" section bold />
          <Row label="Loss incurred" value={r.adjustedLoss} indent />
          <Row label="Amount absorbed" value={r.cyLossAbsorbed} indent negative />
          <Row label="Balance transferred to unabsorbed losses" value={r.cyLossCf} indent bold />
        </>
      ) : (
        r.cyLossAbsorbed > 0 && <Row label="Less: Current year loss absorbed" value={r.cyLossAbsorbed} indent />
      )}
      <Row label="CHARGEABLE INCOME" value={r.chargeableIncome} bold />

      <div className="border-t border-gray-200 my-2" />
      {(r.taxBreakdown || []).map((b, i) => (
        <Row key={i} label={`${b.band} @ ${b.rate}%`} value={b.amount} indent />
      ))}
      <Row label="Gross tax" value={r.grossTax} bold />
      {!!r.section110 && <Row label="Less: Section 110 set off" value={r.section110} indent />}
      {!!r.section132relief && <Row label="Less: Section 132/133 relief" value={r.section132relief} indent />}
      <Row label="Tax charged" value={r.taxAfterSetoff} bold />
      <Row label="Less: Tax paid (CP204)" value={r.cp204Paid} indent negative />

      <div className={`mt-3 rounded-sm p-4 flex items-center justify-between text-white ${r.balance < 0 ? "bg-green-600" : "bg-[#1877c9]"}`}>
        <div>
          <div className="text-sm opacity-90">{r.balance < 0 ? "Tax over paid & refundable" : "Balance tax payable"}</div>
        </div>
        <div className="text-2xl font-bold font-mono">{fmtRM(Math.abs(r.balance))}</div>
      </div>

      {(r.unabsorbedLossCf > 0) && (
        <p className="text-xs text-amber-600 mt-2">Unabsorbed loss carried forward: {fmtRM(r.unabsorbedLossCf)}</p>
      )}
    </div>
  );
};

export default ResultsPanel;
